import React from 'react';
import { ExternalLink, Minus, Plus, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

type ArchitectureFlowDialogProps = {
  children: React.ReactNode;
};

const MIN_ZOOM = 0.6;
const MAX_ZOOM = 2.4;
const ZOOM_STEP = 0.2;
const BASE_WIDTH = 2048;

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export default function ArchitectureFlowDialog({ children }: ArchitectureFlowDialogProps) {
  const [zoom, setZoom] = React.useState(1);

  const zoomIn = () => setZoom((current) => clamp(Number((current + ZOOM_STEP).toFixed(2)), MIN_ZOOM, MAX_ZOOM));
  const zoomOut = () => setZoom((current) => clamp(Number((current - ZOOM_STEP).toFixed(2)), MIN_ZOOM, MAX_ZOOM));
  const resetZoom = () => setZoom(1);

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="flex h-[94vh] max-h-[94vh] flex-col overflow-hidden border-white/10 bg-[linear-gradient(180deg,rgba(5,9,18,0.98),rgba(3,7,14,1))] p-0 text-white shadow-[0_30px_120px_rgba(0,0,0,0.6)] sm:max-w-[96vw]">
        <div className="relative flex h-full min-h-0 flex-col overflow-hidden rounded-lg">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(132,255,76,0.12),transparent_22%),radial-gradient(circle_at_top_right,rgba(56,189,248,0.12),transparent_28%),linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:auto,auto,30px_30px,30px_30px] opacity-60" />

          <div className="relative border-b border-white/10 px-6 py-5 md:px-8">
            <DialogHeader className="gap-4 text-left">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <DialogTitle className="text-3xl font-semibold tracking-[-0.05em] text-white md:text-5xl">
                    MapleOS Overview Architecture
                  </DialogTitle>
                  <DialogDescription className="mt-3 max-w-3xl text-sm leading-6 text-white/60 md:text-base">
                    Zoom in and scroll around to inspect how MapleOS works.
                  </DialogDescription>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-white/70">
                    Zoom {Math.round(zoom * 100)}%
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-white"
                    onClick={zoomOut}
                    disabled={zoom <= MIN_ZOOM}
                    aria-label="Zoom out architecture diagram"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-white"
                    onClick={zoomIn}
                    disabled={zoom >= MAX_ZOOM}
                    aria-label="Zoom in architecture diagram"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-white"
                    onClick={resetZoom}
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Reset
                  </Button>
                </div>
              </div>
            </DialogHeader>
          </div>

          <div className="relative min-h-0 flex-1 p-4 md:p-6">
            <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
              <div className="border-b border-white/10 px-4 py-3 text-xs text-white/55 md:px-5">
                Tip: zoom in, then scroll horizontally and vertically to inspect the full architecture.
              </div>

              <div className="min-h-0 flex-1 overflow-auto">
                <div className="flex min-h-full min-w-full items-start justify-start p-4 md:p-6">
                  <img
                    src="/images/mapleos/mapleos-architecture-diagram-2026-04-02.png"
                    alt="MapleOS overview architecture diagram"
                    className="h-auto max-w-none rounded-2xl border border-white/10 bg-black/40 shadow-[0_20px_80px_rgba(0,0,0,0.45)]"
                    style={{ width: `${Math.round(BASE_WIDTH * zoom)}px` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
